// Microsoft Clarity loader — production-only.
//
// Clarity records session replays + heatmaps + interaction analytics. We
// load it ONLY in production builds (VITE_DEPLOY_ENV === "production") so
// staging + dev sessions don't pollute the production property and so
// session-replay never captures an operator running against the sandbox
// or against a local dev backend.
//
// The script is loaded asynchronously after first paint to keep the
// initial render path free of third-party blocking. We use the standard
// Clarity bootstrap snippet but rewritten in typed TS — the IIFE form
// Clarity ships is structurally identical.

import { deployEnv } from "./deployEnv";

// Project tag provided by Microsoft Clarity (panel.pay.prod.khazana.cards
// property). If you ever rotate the property, just bump this constant.
const CLARITY_PROJECT_ID = "wxo27xfdpk";

// Augment window so TS knows about the clarity queue Microsoft installs.
// The queue takes any args, then the loaded script replaces it with a
// real function. We model it as a loose callable + a `q` array.
type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };
declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

let injected = false;

/**
 * initClarity injects the Microsoft Clarity script in production builds.
 * Safe to call multiple times — the `injected` guard makes subsequent
 * calls cheap no-ops. No-op in staging + dev.
 */
export function initClarity(): void {
  if (injected) return;
  if (deployEnv !== "production") return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  injected = true;

  // Install the queue shim BEFORE the real script lands. Any calls made
  // before the script loads (e.g. clarity("identify", ...)) accumulate
  // here and replay once the real script attaches.
  const stub: ClarityFn = function (...args: unknown[]) {
    (stub.q = stub.q || []).push(args);
  };
  window.clarity = window.clarity || stub;

  const tag = document.createElement("script");
  tag.async = true;
  tag.src = "https://www.clarity.ms/tag/" + CLARITY_PROJECT_ID;
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(tag, first);
  } else {
    document.head.appendChild(tag);
  }
}
